import { css } from '@emotion/react';
import {
  motion, useAnimate,
} from 'motion/react';
import {
  type FC, useEffect,
} from 'react';
import { LinkComponent } from '~/_shared/baseComponents/links';
import {
  LottieAnimationComponent, LottieAnimationTypes,
} from '~/_shared/baseComponents/lottieAnimation';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';

const fullWidthStyle = css({ width: '100%' });

const resendLinkStyle = ({ isDisabled }: { isDisabled: boolean }) => (theme: Theme) => css({
  cursor: isDisabled ? 'not-allowed' : 'default',
  opacity: isDisabled ? theme.opacity.disabled : 1,
  textDecoration: 'underline',
});

const mfaSentStyle = (theme: Theme) => css({
  color: theme.textColors.success,
  cursor: 'default',
  display: 'flex',
  gap: '8px',
});

type EmailSentAnimatedTextProps = Readonly<{
  playAnimation: boolean;
  text: string;

  onAnimationEnd?: () => void;
  onResend: () => void;
}>;

export const EmailSentAnimatedTextComponent: FC<EmailSentAnimatedTextProps> = props => {
  const [t] = useTranslation();
  const [scope, animate] = useAnimate();

  useEffect(() => {
    if (props.playAnimation) {
      animate(scope.current, {
        opacity: 1,
        transition: {
          from: 0.4,
          duration: 0.3,
        },
      });
    }
  }, [props.playAnimation, animate, scope]);

  return (
    <motion.div
      ref={scope}
      initial={{ opacity: .4 }}
      transition={{ duration: .3 }}
      css={mfaSentStyle}
    >
      <LottieAnimationComponent
        autoplay={props.playAnimation}
        onAnimationEnd={props.onAnimationEnd}
        size={30}
        type={LottieAnimationTypes.MailChecked}
      />
      <div css={fullWidthStyle}>
        {props.text}
        <br />
        <LinkComponent
          disabled={props.playAnimation}
          css={resendLinkStyle({ isDisabled: props.playAnimation })}
          onClick={props.onResend}
          path={null}
        >
          {t('Resend Email')}
        </LinkComponent>
      </div>
    </motion.div>
  );
};
